import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Sidebar,
  BatchItem,
  ProgressBar,
  IconWrapper,
  BatchCurrentStatus,
  NoRecord,
  SidebarToolbar,
  SidebarArchiveButton,
  SidebarArchivePopup,
  ArchiveIconButton,
} from './Styles';
import { PanelLeftCloseIcon, PanelRightCloseIcon, TickSVG, RejectionSVG, ArchiveSVG, UnArchiveSVG } from '../../../images/svgIcons';
import { Pagination, Empty, Tooltip, Button, Checkbox, Icon, message, Badge } from 'antd';
import { useSubscription } from '@apollo/client';
import { FETCH_BATCHES } from '../gql/FetchBatches';
import moment from 'moment';
import { getProgressPercentage, getStatusColor, getStatusMessage } from '../../../utils/helper';
import { withTranslation } from 'react-i18next';
import BatchDetailsPanel from './JobDetails';
import { STATUS_MONITORING } from './TranslationValues';
import { archiveBatches } from '../../../utils/services/statusMonitoringAPI';

const NAMESPACES = ['STATUS_MONITORING'];
const SIDEBAR_PAGINATION_HEIGHT = 52;
const ARCHIVE_ELIGIBLE_STATUSES = new Set([
  'aborted',
  'cancelled',
  'processing_complete',
  'processing_completed',
  'reject',
  'rejected',
  'released',
]);

const normalizeStatus = status => status?.toLowerCase?.() || '';

const isArchiveActionAvailable = (status, isArchiveMode, isJobManager) => {
  const normalizedStatus = normalizeStatus(status);
  return (
    isJobManager &&
    (isArchiveMode ||
      ARCHIVE_ELIGIBLE_STATUSES.has(normalizedStatus) ||
      normalizedStatus.startsWith('reject'))
  );
};

const getBatchSelection = batch => ({
  jobId: batch?.id,
  batchType: batch?.batchType,
});

const getBatchSelectionKey = batch => `${batch?.batchType || ''}:${batch?.jobId ?? batch?.id ?? ''}`;

const uniqueBatchSelections = items => Array.from(
  new Map(items.map(item => [getBatchSelectionKey(item), item])).values()
);

const BatchCard = ({
  batchData,
  activeItem,
  setActiveItem,
  setBatchType,
  index = 1,
  t,
  setBatchDetailsLoading,
  isArchiveMode,
  isSelectedForArchive,
  onArchiveToggle,
  isJobManager,
}) => {
  const cardRef = useRef(null);

  const onBatchCardClick = useCallback(
    e => {
      setBatchDetailsLoading(true);
      const ref = e.currentTarget;
      cardRef.current.classList.add('clicking');
      setTimeout(() => {
        cardRef.current.classList.remove('clicking');
        setTimeout(() => {
          setActiveItem(batchData?.id);
          setBatchType(batchData?.batchType);
          setBatchDetailsLoading(false);
        }, 0);
      }, 100);
    },
    [batchData, setActiveItem, setBatchDetailsLoading, setBatchType],
  );

  const onArchiveIconClick = useCallback(
    e => {
      e.stopPropagation();
      onArchiveToggle(batchData);
    },
    [batchData, onArchiveToggle],
  );

  const memoizedBatchCard = useMemo(() => {
    const { id, batchName, createdTime, totalCount, batchStats, status, batchType, channelType } = batchData;
    const percentage = getProgressPercentage(totalCount, batchStats);
    const showArchiveAction = isArchiveActionAvailable(status, isArchiveMode, isJobManager);
    const archiveActionLabel = isArchiveMode
      ? t(STATUS_MONITORING.UNARCHIVE.key, STATUS_MONITORING.UNARCHIVE.value)
      : t(STATUS_MONITORING.ARCHIVE.key, STATUS_MONITORING.ARCHIVE.value);

    return (
      <BatchItem
        key={id}
        ref={cardRef}
        className={'animate'}
        index={index}
        active={id === activeItem}
        onClick={onBatchCardClick}
      >
        <div className="batch-title">
          <h6 title={batchName}>{batchName}</h6>
          <div>
            {status === 'processing_complete' ? (
              <Tooltip placement='top' title={getStatusMessage(status, t)}>
                <IconWrapper background={'#C8E066'}>
                  <TickSVG />
                </IconWrapper>
              </Tooltip>
            ): normalizeStatus(status).includes('reject') && (
              <Tooltip placement='top' title={getStatusMessage(status, t)}>
                <span style={{ display: 'inline-block', marginBottom:'-14px' }}>
                  <RejectionSVG/>
                </span>
              </Tooltip>
            )}
          </div>
        </div> 
        <p>{moment(createdTime).format('Do MMM YYYY')} • {moment(createdTime).format('LT')}</p>  
        <div className="batch-status-row">
          <div className="batch-status-content">
            {status === 'in_progress' || status === 'processing_complete' ? (
              <div className="progress-bar">
                <ProgressBar value={percentage}>
                  <div></div>
                </ProgressBar>
                <strong>{percentage}%</strong>
              </div>
            ) : (
              getStatusMessage(status, t) && (
                <BatchCurrentStatus bg={getStatusColor(status)} status={status}>
                  {getStatusMessage(status, t)}
                </BatchCurrentStatus>
              )
              )}
          </div>
          {showArchiveAction && (
            <Tooltip placement="top" title={archiveActionLabel}>
              <ArchiveIconButton
                type="button"
                selected={isSelectedForArchive}
                archiveMode={isArchiveMode}
                onClick={onArchiveIconClick}
                aria-label={archiveActionLabel}
              >
                {isSelectedForArchive ? <Icon type="check" /> : isArchiveMode ? <UnArchiveSVG size={14}  color={'#6d28d9'}/> : <ArchiveSVG size={14}  color={'#6d28d9'}/>}
              </ArchiveIconButton>
            </Tooltip>
          )}
        </div>
      </BatchItem>
    );
  }, [batchData, activeItem, t, isArchiveMode, isSelectedForArchive, onArchiveIconClick, index, onBatchCardClick]);
  return <>{memoizedBatchCard}</>;
};

const StatusMonitoringContent = ({ filters, setFilters, cardCount, t, users, showSidebar, setShowSidebar, setHybridInfo, setCurrentPage, setDraftBatchInfo, isJobManager }) => {
  const [total, setTotal] = useState(0);

  const [activeItem, setActiveItem] = useState(null);
  const [batchType, setBatchType] = useState(null);
  const [subscribed, setSubscribed] = useState(true);
  const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);
  const [archiveSelection, setArchiveSelection] = useState({ isSelectedAll: false, items: [] });
  const [showArchivePopup, setShowArchivePopup] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const handlePageChange = page => {
    setFilters(prevFilter => ({ filter: { ...prevFilter.filter, page: page } }));
    setTimeout(() => {
      setActiveItem(null);
      setBatchType(null);
    }, 1000);
  };

  const { data, loading, error } = useSubscription(FETCH_BATCHES, {
    variables: filters,
    skip: !subscribed,
    shouldResubscribe: true,
  });

  const batchList = data?.batchDocumentStats?.data || [];
  const isArchiveMode = Boolean(filters?.filter?.isArchived);
  const archiveActionLabel = isArchiveMode
    ? t(STATUS_MONITORING.UNARCHIVE.key, STATUS_MONITORING.UNARCHIVE.value)
    : t(STATUS_MONITORING.ARCHIVE.key, STATUS_MONITORING.ARCHIVE.value);
  const archiveSuccessMessage = isArchiveMode
    ? t(STATUS_MONITORING.UNARCHIVE_SUCCESS.key, STATUS_MONITORING.UNARCHIVE_SUCCESS.value)
    : t(STATUS_MONITORING.ARCHIVE_SUCCESS.key, STATUS_MONITORING.ARCHIVE_SUCCESS.value);
  const selectableBatches = useMemo(
    () => batchList.filter(batch => isArchiveActionAvailable(batch?.status, isArchiveMode)),
    [batchList, isArchiveMode],
  );
  const selectedBatchKeys = useMemo(
    () => new Set(archiveSelection.items.map(item => getBatchSelectionKey(item))),
    [archiveSelection.items],
  );
  const selectedArchiveCount = archiveSelection.isSelectedAll ? total : archiveSelection.items.length;
  const archiveSelectionFilterKey = useMemo(() => {
    const { page, limit, ...filterPayload } = filters?.filter || {};
    return JSON.stringify(filterPayload);
  }, [filters]);

  const resetArchiveSelection = useCallback(() => {
    setArchiveSelection({ isSelectedAll: false, items: [] });
    setShowArchivePopup(false);
  }, []);

  const handleArchiveToggle = useCallback(
    batch => {
      setShowArchivePopup(true);
      setArchiveSelection(prevSelection => {
        if (prevSelection.isSelectedAll) {
          const currentPageItems = selectableBatches
            .filter(item => getBatchSelectionKey(item) !== getBatchSelectionKey(batch))
            .map(getBatchSelection);
          return {
            isSelectedAll: false,
            items: uniqueBatchSelections(currentPageItems),
          };
        }

        const selectedItem = getBatchSelection(batch);
        const selectedKey = getBatchSelectionKey(selectedItem);
        const isAlreadySelected = prevSelection.items.some(item => getBatchSelectionKey(item) === selectedKey);
        const items = isAlreadySelected
          ? prevSelection.items.filter(item => getBatchSelectionKey(item) !== selectedKey)
          : uniqueBatchSelections([...prevSelection.items, selectedItem]);

        return {
          isSelectedAll: false,
          items,
        };
      });
    },
    [selectableBatches],
  );

  const handleSelectAllChange = e => {
    setShowArchivePopup(true);
    setArchiveSelection({ isSelectedAll: e.target.checked, items: [] });
  };

  const getArchivePayload = () => {
    const { page, limit, ...activeFilters } = filters?.filter || {};
    const selectedItems = archiveSelection.isSelectedAll ? [] : archiveSelection.items;

    return {
      ...activeFilters,
      archive: !isArchiveMode,
      selectAll: archiveSelection.isSelectedAll,
      hybridBatchIds: selectedItems
        .filter(item => item.batchType?.toUpperCase?.() === 'HYBRID')
        .map(item => item.jobId),
      advancedBatchIds: selectedItems
        .filter(item => item.batchType?.toUpperCase?.() !== 'HYBRID')
        .map(item => item.jobId),
    };
  };

  const setActiveBatch = () => {
    const items = data?.batchDocumentStats?.data || [];
    const currentIndex = items.findIndex(item => item.id === activeItem);
    let nextItemId = null;
    let nextBatchType = null;
    if (currentIndex !== -1) {
      if (currentIndex < items.length - 1) {
        nextItemId = items[currentIndex + 1]?.id;
        nextBatchType = items[currentIndex + 1]?.batchType;
      } else if (items.length > 1) {
        nextItemId = items[currentIndex - 1]?.id;
        nextBatchType = items[currentIndex - 1]?.batchType;
      }
    }
    setTimeout(() => {
      setActiveItem(nextItemId);
      setBatchType(nextBatchType);
    }, 500);
  };

  const handleArchiveSubmit = async () => {
    if (!selectedArchiveCount) return;
    setArchiveLoading(true);
    try {
      const payload = getArchivePayload();
      const response = await archiveBatches(payload);
      if (response?.statusCode === 7037 || response?.statusCode === 7038) {
        message.success(archiveSuccessMessage);
        resetArchiveSelection();
        setActiveBatch();
      } else {
        message.error(
          t(STATUS_MONITORING.ARCHIVE_ERROR.key, STATUS_MONITORING.ARCHIVE_ERROR.value),
        );
      }
    } catch (e) {
      message.error(t(STATUS_MONITORING.ARCHIVE_ERROR.key, STATUS_MONITORING.ARCHIVE_ERROR.value));
    } finally {
      setArchiveLoading(false);
    }
  };

  useEffect(() => {
    if (activeItem === null) {
      setActiveItem(data?.batchDocumentStats?.data ? data?.batchDocumentStats?.data[0]?.id : null);
      setBatchType(data?.batchDocumentStats?.data ? data?.batchDocumentStats?.data[0]?.batchType : null);
    }
    if (data?.batchDocumentStats?.statusCode === 4005) {
      setTotal(data?.batchDocumentStats?.dataCount);
    }
  }, [data, activeItem]);

  useEffect(() => {
    activeItem && setActiveItem(null);
    batchType && setBatchType(null);
  }, [filters]);

  useEffect(() => {
    resetArchiveSelection();
  }, [archiveSelectionFilterKey, resetArchiveSelection]);
  // if (error) return <p>Error: {error.message}</p>;
  return (
    <>
      {data?.batchDocumentStats?.data?.length !== 0 &&
      data?.batchDocumentStats?.statusCode !== 4006 ? (
        <>
          <button className='sidebar_toggle' onClick={() => setShowSidebar(!showSidebar)}>
            {showSidebar ? PanelLeftCloseIcon() : PanelRightCloseIcon()}
          </button>
          <Sidebar pageNumberHeight={SIDEBAR_PAGINATION_HEIGHT} showSidebar={showSidebar}>
            <>
              <SidebarToolbar>
                {(isJobManager && !showArchivePopup) && (
                  <SidebarArchiveButton type="button" archiveMode={isArchiveMode} onClick={() => setShowArchivePopup(true)}>
                    {isArchiveMode ? <UnArchiveSVG size={14} color={'#6d28d9'}/> : <ArchiveSVG size={14} color={'#6d28d9'} />}
                    <span>{archiveActionLabel}</span>
                  </SidebarArchiveButton>
                )}
              </SidebarToolbar>
              {showArchivePopup && (
                <SidebarArchivePopup>
                  <div className="archive-popup-actions">
                    <Checkbox checked={archiveSelection.isSelectedAll} onChange={handleSelectAllChange}>
                      {t(STATUS_MONITORING.SELECT_ALL.key, STATUS_MONITORING.SELECT_ALL.value)}
                    </Checkbox>
                    <div>
                      <Button size="small" onClick={resetArchiveSelection} style={{ marginRight: '8px' }}>
                        {t(STATUS_MONITORING.CANCEL.key, STATUS_MONITORING.CANCEL.value)}
                      </Button>
                      <Badge showZero={false} count={archiveSelection.isSelectedAll ? 0 : selectedArchiveCount} style={{ backgroundColor: 'purple' }}>
                        <Button
                          size="small"
                          type="primary"
                          loading={archiveLoading}
                          disabled={!selectedArchiveCount}
                          onClick={handleArchiveSubmit}
                        >
                          {archiveActionLabel}
                        </Button>
                      </Badge>
                    </div>
                  </div>
                </SidebarArchivePopup>
              )}
              {data?.batchDocumentStats?.statusCode === 4005 &&
                data?.batchDocumentStats?.data?.map((batch, index) => (
                  <BatchCard
                    batchData={batch}
                    activeItem={activeItem}
                    setActiveItem={setActiveItem}
                    setBatchType={setBatchType}
                    key={batch.id}
                    index={index}
                    t={t}
                    setBatchDetailsLoading={setBatchDetailsLoading}
                    isArchiveMode={isArchiveMode}
                    isSelectedForArchive={
                      archiveSelection.isSelectedAll || selectedBatchKeys.has(getBatchSelectionKey(batch))
                    }
                    onArchiveToggle={handleArchiveToggle}
                    isJobManager={isJobManager}
                  />
                ))}
              <div className="pagination-wrapper">
                <Pagination
                  size={'small'}
                  defaultCurrent={1}
                  total={total}
                  pageSize={cardCount}
                  onChange={e => handlePageChange(e)}
                  current={filters?.filter?.page}
                />
              </div>
            </>
          </Sidebar>
          {/* <StatusMonitoringInfo
            jobId={activeItem}
            users={users}
            subscribed={subscribed}
            filters={filters}
          /> */}
            {
              batchDetailsLoading ?
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                  }}
                >
                  <img src={'tech_loader.gif'} style={{ width: '100px' }} alt="" />
                </div> :
                <BatchDetailsPanel
                  jobId={activeItem}
                  batchType={batchType}
                  users={users}
                  subscribed={subscribed}
                  filters={filters}
                  cardCount={cardCount}
                  setHybridInfo={setHybridInfo}
                  setCurrentPage={setCurrentPage}
                  setDraftBatchInfo={setDraftBatchInfo}
                  batchData={data}
                  setActiveItem={setActiveItem}
                  setBatchType={setBatchType}
                  showSidebar={showSidebar}
                  isArchiveMode={isArchiveMode}
                />
            }
        </>
      ) : (
        <NoRecord>
          <Empty description={t(STATUS_MONITORING.NO_JOBS_FOUND.key, STATUS_MONITORING.NO_JOBS_FOUND.value)} />
        </NoRecord>
      )}
    </>
  );
};

export default withTranslation(NAMESPACES)(StatusMonitoringContent);
